import type { EngineResult, ScheduleRow, SeatOut } from '@conciair/scheduler-core';
import type { ScheduleAssignmentRow, Seat, TablesInsert } from '../data/database.types';

/** Lookups the mapping needs: tail number -> aircraft id, pilot code -> pilot id. */
export interface IdMaps {
  aircraftIdByTail: Record<string, string>;
  pilotIdByCode: Record<string, string>;
}

/**
 * Engine schedule rows -> schedule_assignments rows (one per window x seat).
 * Throws when a tail or pilot code is unknown, which can only happen if the
 * roster changed between snapshot and store.
 */
export function toAssignmentRows(
  orgId: string,
  runId: string,
  result: EngineResult,
  ids: IdMaps
): TablesInsert<'schedule_assignments'>[] {
  const rows: TablesInsert<'schedule_assignments'>[] = [];
  const pilotId = (code: string | null): string | null => {
    if (!code) return null;
    const id = ids.pilotIdByCode[code];
    if (!id) throw new Error('Unknown pilot code in schedule: ' + code);
    return id;
  };
  for (const row of result.schedule) {
    const aircraftId = ids.aircraftIdByTail[row.tail];
    if (!aircraftId) throw new Error('Unknown tail in schedule: ' + row.tail);
    for (const seat of ['PIC', 'SIC'] as const) {
      const a: SeatOut = seat === 'PIC' ? row.pic : row.sic;
      const partner: SeatOut = seat === 'PIC' ? row.sic : row.pic;
      rows.push({
        org_id: orgId,
        run_id: runId,
        aircraft_id: aircraftId,
        type_code: row.type,
        rotation_start: row.rotationStart,
        rotation_end: row.rotationEnd,
        seat,
        pilot_id: pilotId(a.pilotId),
        seat_start: a.start || row.rotationStart,
        seat_end: a.end || row.rotationEnd,
        days: a.days ?? 8,
        absorbed: !!a.absorbed,
        pinned: !!a.pinned,
        double: !!a.double,
        supervised: !!a.supervised,
        acting_tc: !!a.actingTC,
        partner_pilot_id: pilotId(partner.pilotId),
        inserted: !!row.inserted,
      });
    }
  }
  return rows;
}

/** Stored assignment rows -> engine schedule rows, for the Gantt, grids, and rebuildCarry. */
export function fromAssignmentRows(
  rows: readonly Pick<ScheduleAssignmentRow, 'aircraft_id' | 'type_code' | 'rotation_start' | 'rotation_end' | 'seat' | 'pilot_id' | 'seat_start' | 'seat_end' | 'days' | 'absorbed' | 'pinned' | 'double' | 'supervised' | 'acting_tc' | 'inserted'>[],
  ids: { tailByAircraftId: Record<string, string>; codeByPilotId: Record<string, string> }
): ScheduleRow[] {
  const byWindow = new Map<string, ScheduleRow>();
  const emptySeat = (start: string, end: string): SeatOut => ({
    pilotId: null, supervised: false, double: false, pinned: false, actingTC: false,
    start, end, days: 8, absorbed: false,
  });
  for (const r of rows) {
    const tail = ids.tailByAircraftId[r.aircraft_id] ?? r.aircraft_id;
    const key = tail + '|' + r.rotation_start;
    let row = byWindow.get(key);
    if (!row) {
      row = {
        rotationStart: r.rotation_start,
        rotationEnd: r.rotation_end,
        tail,
        type: r.type_code,
        pic: emptySeat(r.rotation_start, r.rotation_end),
        sic: emptySeat(r.rotation_start, r.rotation_end),
        inserted: !!r.inserted,
      };
      byWindow.set(key, row);
    }
    const seat: SeatOut = {
      pilotId: r.pilot_id ? (ids.codeByPilotId[r.pilot_id] ?? r.pilot_id) : null,
      supervised: r.supervised,
      double: r.double,
      pinned: r.pinned,
      actingTC: r.acting_tc,
      start: r.seat_start,
      end: r.seat_end,
      days: r.days,
      absorbed: r.absorbed,
    };
    if ((r.seat as Seat) === 'PIC') row.pic = seat;
    else row.sic = seat;
  }
  return Array.from(byWindow.values()).sort(
    (a, b) => a.rotationStart.localeCompare(b.rotationStart) || a.tail.localeCompare(b.tail)
  );
}
